import { useState, useEffect } from "react";
import { X, Save, Loader2, Plus, ImagePlus, Globe } from "lucide-react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { logActivity } from "../lib/activity-logger";
import type { ClaimsProject } from "../types/database.types";
import {
  DEFAULT_PAYMENT_PERIOD_DAYS,
  resolveClaimDay,
  resolvePaymentPeriodDays,
} from "../lib/claimTiming";
import { claimTimingDb } from "../lib/claimTimingDb";
import { retentionCeilingAmount, formatRetentionCeilingPercent, normalizeRetentionCeilingPercent, parseRetentionCeilingPercentInput } from "../lib/retentionCeiling";
import { useProjectImages } from "../hooks/useProjectImages";
import { ProjectImageUploader } from "./ProjectImageUploader";
import { WebDescriptionEditor } from "./WebDescriptionEditor";
import { ProjectPeopleFields } from "./ProjectPeopleFields";

// Accept ClaimsProject or any type that extends it
interface EditProjectModalProps {
  project: ClaimsProject;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /**
   * SEQ-169 follow up. Claim day and payment period are commercial terms: they
   * set the date on every claim and the date every invoice falls due. The rest
   * of this modal is job location, addresses, builder contacts, website copy,
   * images and status, which staff without financial access legitimately edit,
   * so the gate is on the claim timing block rather than on the whole modal.
   *
   * This is the display half only. The authority is the RLS policy set by
   * 20260915000200, which refuses the write whatever the frontend sends.
   */
  canEditClaimTiming: boolean;
}

interface DiscountItem {
  id?: string;
  description: string;
  amount: number | string;
}

export function EditProjectModal({
  project,
  isOpen,
  onClose,
  onSaved,
  canEditClaimTiming,
}: EditProjectModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [jobLocation, setJobLocation] = useState("");
  const [jobAddress, setJobAddress] = useState("");
  const [projectDetails, setProjectDetails] = useState("");
  // Public website fields (kept separate from internal project_details).
  const [webDescription, setWebDescription] = useState("");
  const [showOnWeb, setShowOnWeb] = useState(true);
  const [webTitle, setWebTitle] = useState("");
  const [webSuburb, setWebSuburb] = useState("");
  const [webRegion, setWebRegion] = useState("");
  const [webType, setWebType] = useState("");
  const [normalRate, setNormalRate] = useState("");
  const [overtimeRate, setOvertimeRate] = useState("");
  const [saturdayRate, setSaturdayRate] = useState("");
  const [saturdayContractOTRate, setSaturdayContractOTRate] = useState("");
  const [claimDay, setClaimDay] = useState("");
  // SEQ-169: both of these live in claims_project_config, not on the project
  // row. claims_projects.claim_day is a derived mirror kept current by a
  // database trigger, so it is no longer written from here.
  const [paymentPeriodDays, setPaymentPeriodDays] = useState(
    DEFAULT_PAYMENT_PERIOD_DAYS.toString(),
  );
  const [retentionCeilingMode, setRetentionCeilingMode] = useState<"original" | "revised" | "custom">("original");
  const [retentionCustomAmount, setRetentionCustomAmount] = useState("");
  const [retentionCeilingPercent, setRetentionCeilingPercent] = useState("5");
  const [status, setStatus] = useState<"active" | "completed" | "on_hold" | "upcoming">(
    "active",
  );
  const [builderName, setBuilderName] = useState("");
  const [builderAddress, setBuilderAddress] = useState("");
  // SEQ-180 phase 2, P22.
  const [projectManager, setProjectManager] = useState("");
  const [siteManager, setSiteManager] = useState("");
  const [builderPocName, setBuilderPocName] = useState("");
  const [builderEmail, setBuilderEmail] = useState("");

  // Discounts state
  const [discounts, setDiscounts] = useState<DiscountItem[]>([]);
  const [originalDiscounts, setOriginalDiscounts] = useState<DiscountItem[]>(
    [],
  );

  // Project images
  const { images, uploading, upload, remove, makeCover, reorder } = useProjectImages(
    isOpen ? project.id : undefined
  );

  // Initialize form with project data
  useEffect(() => {
    if (project && isOpen) {
      setJobLocation(project.job_location || "");
      setJobAddress(project.job_address || "");
      setProjectDetails(project.project_details || "");
      setWebDescription(project.web_description || "");
      setShowOnWeb(project.show_on_web ?? true);
      setWebTitle(project.web_title || "");
      setWebSuburb(project.web_suburb || "");
      setWebRegion(project.web_region || "");
      setWebType(project.web_type || "");
      setNormalRate((project.normal_rate || 100).toString());
      setOvertimeRate((project.overtime_rate || 150).toString());
      setSaturdayRate((project.saturday_rate || 400).toString());
      setSaturdayContractOTRate(
        project.saturday_contract_ot_rate
          ? project.saturday_contract_ot_rate.toString()
          : ""
      );
      setClaimDay((project.claim_day ?? 26).toString());
      setPaymentPeriodDays(DEFAULT_PAYMENT_PERIOD_DAYS.toString());

      // SEQ-169 claim timing. The project row is only the mirror, so the config
      // record wins when it exists. Before the migration is applied it does
      // not, and the mirrored claim_day set just above stands.
      const fetchTiming = async () => {
        const { data } = await claimTimingDb
          .from("claims_project_config")
          .select("default_claim_day, payment_period_days")
          .eq("project_id", project.id)
          .maybeSingle();

        if (data) {
          setClaimDay(data.default_claim_day.toString());
          setPaymentPeriodDays(data.payment_period_days.toString());
        }
      };
      fetchTiming();
      const mode = (project.retention_ceiling_mode || (project.retention_on_revised ? 'revised' : 'original')) as "original" | "revised" | "custom";
      setRetentionCeilingMode(mode);
      setRetentionCustomAmount(project.retention_custom_amount ? String(project.retention_custom_amount) : "");
      setRetentionCeilingPercent(formatRetentionCeilingPercent(project.retention_ceiling_percent));

      const s = project.status as "active" | "completed" | "on_hold" | "upcoming";
      setStatus(["active", "completed", "on_hold", "upcoming"].includes(s) ? s : "active");

      setProjectManager(project.project_manager || "");
      setSiteManager(project.site_manager || "");

      setBuilderName(project.builder_name || "");
      setBuilderAddress(project.builder_address || "");
      setBuilderPocName(project.builder_poc_name || "");
      setBuilderEmail(project.builder_email || "");

      // Fetch discounts
      const fetchDiscounts = async () => {
        const { data } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          .from("claims_discounts" as any)
          .select("*")
          .eq("project_id", project.id);

        if (data) {
          setDiscounts(data as unknown as DiscountItem[]);
          setOriginalDiscounts(data as unknown as DiscountItem[]);
        }
      };
      fetchDiscounts();

      setError(null);
    }
  }, [project, isOpen]);

  const handleSave = async () => {
    if (!jobLocation.trim()) {
      setError("Job Location is required");
      return;
    }

    // SEQ-235: the percentage is a financial term. Only a user with financial
    // access can change it (the database refuses anyone else), so it is sent
    // only then and only when it differs from what was loaded.
    const pctInput = parseRetentionCeilingPercentInput(retentionCeilingPercent);
    if (canEditClaimTiming && !pctInput.ok) {
      setError(pctInput.message);
      return;
    }
    const pctChanged =
      canEditClaimTiming &&
      pctInput.ok &&
      pctInput.value !== normalizeRetentionCeilingPercent(project?.retention_ceiling_percent);

    setLoading(true);
    setError(null);

    try {
      // 1. Update Project Details
      const { data: updatedRows, error: updateError } = await supabase
        .from("claims_projects")
        .update({
          job_location: jobLocation,
          job_address: jobAddress,
          project_details: projectDetails,
          web_description: webDescription || null,
          show_on_web: showOnWeb,
          web_title: webTitle || null,
          web_suburb: webSuburb || null,
          web_region: webRegion || null,
          web_type: webType || null,
          normal_rate: parseFloat(normalRate) || 100,
          overtime_rate: parseFloat(overtimeRate) || 150,
          saturday_rate: parseFloat(saturdayRate) || 400,
          saturday_contract_ot_rate: saturdayContractOTRate ? parseFloat(saturdayContractOTRate) : null,
          retention_on_revised: retentionCeilingMode === 'revised',
          retention_ceiling_mode: retentionCeilingMode,
          retention_custom_amount: retentionCeilingMode === 'custom' ? (parseFloat(retentionCustomAmount) || null) : null,
          ...(pctChanged && pctInput.ok ? { retention_ceiling_percent: pctInput.value } : {}),
          status: status,
          builder_name: builderName || null,
          builder_address: builderAddress || null,
          builder_poc_name: builderPocName || null,
          builder_email: builderEmail || null,
          project_manager: projectManager.trim() || null,
          site_manager: siteManager.trim() || null,
        })
        .eq("id", project.id)
        // SEQ-180 phase 2, P22. A bare .update() returns no error when the
        // RLS UPDATE policy matches zero rows, so the modal used to close
        // on a save that never happened and the values reverted on the next
        // read (the recorded project-rate failure). Selecting the id back
        // turns that into a visible failure: check the error AND the row
        // count, because only the second one catches this case.
        .select("id");

      if (updateError) throw updateError;
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error(
          "This project was not updated. You may not have permission to edit it. Nothing has been saved.",
        );
      }

      // 2. SEQ-169 claim timing, in claims_project_config.
      //
      // Skipped outright without financial access. The fields are not shown to
      // that user and the values in state are whatever was read back, so there
      // is nothing to save; sending the write anyway would be refused by the
      // RLS policy set by 20260915000200 and would fail a save of the job
      // location and builder details that the user is entitled to make.
      //
      // claims_projects.claim_day is deliberately not written here any more: a
      // database trigger mirrors it from default_claim_day, so writing both
      // would give the dashboard badge two sources that can disagree.
      //
      // The returned row is checked rather than only the error, because an RLS
      // policy that matches no row makes supabase-js report success on an
      // update that changed nothing. That is the silent revert already seen on
      // project rates, and a claim day that silently fails to save would move
      // real claim dates.
      if (canEditClaimTiming) {
        const timingOrgId = requireOrganizationId();
        const { data: savedTiming, error: timingError } = await claimTimingDb
          .from("claims_project_config")
          .upsert(
            {
              project_id: project.id,
              organization_id: timingOrgId,
              default_claim_day: resolveClaimDay(claimDay, project.claim_day ?? 26),
              payment_period_days: resolvePaymentPeriodDays(paymentPeriodDays),
            },
            { onConflict: "project_id" },
          )
          .select("default_claim_day, payment_period_days")
          .maybeSingle();

        if (timingError) throw timingError;
        if (!savedTiming) {
          throw new Error(
            "Claim timing was not saved. You may not have permission to change this project's settings.",
          );
        }
        setClaimDay(savedTiming.default_claim_day.toString());
        setPaymentPeriodDays(savedTiming.payment_period_days.toString());
      }

      // 3. Handle Discounts
      // Identify deleted discounts
      const originalIds = new Set(originalDiscounts.map((d) => d.id));
      const currentIds = new Set(
        discounts.filter((d) => d.id).map((d) => d.id),
      );
      const toDelete = [...originalIds].filter((id) => !currentIds.has(id));

      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          .from("claims_discounts" as any)
          .delete()
          .in("id", toDelete);
        if (deleteError) throw deleteError;
      }

      // Upsert current discounts
      const orgId = requireOrganizationId();
      const toUpsert = discounts
        .filter((d) => d.description.trim() && d.amount)
        .map((d) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          const payload: any = {
            organization_id: orgId,
            project_id: project.id,
            description: d.description,
            amount:
              typeof d.amount === "string" ? parseFloat(d.amount) : d.amount,
          };
          if (d.id) {
            payload.id = d.id;
          }
          return payload;
        });

      console.log(`[EditProject] Upserting ${toUpsert.length} discounts`);

      if (toUpsert.length > 0) {
        const { error: upsertError } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          .from("claims_discounts" as any)
          .upsert(toUpsert);
        if (upsertError) throw upsertError;
      }

      // Log Activity: Update Project
      await logActivity({
        projectId: project.id,
        actionType: "UPDATE",
        entityType: "PROJECT",
        entityId: project.id,
        description: `Updated details for project "${jobLocation}"`,
      });

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 600, maxHeight: "90vh", overflow: "auto" }}
      >
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">Edit Project Details</h3>
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                color: "var(--color-error)",
                padding: "var(--spacing-md)",
                borderRadius: "var(--border-radius-md)",
                marginBottom: "var(--spacing-lg)",
              }}
            >
              {error}
            </div>
          )}

          {/* Project Details */}
          <div className="form-group">
            <label htmlFor="edit-jobLocation" className="form-label">
              Job Location / Project Name *
            </label>
            <input
              id="edit-jobLocation"
              type="text"
              className="form-input"
              value={jobLocation}
              disabled
              title="Cannot be edited as this links to Dashpivot"
              style={{
                opacity: 0.7,
                cursor: "not-allowed",
                background: "var(--bg-secondary)",
              }}
            />
            <p
              style={{
                fontSize: "0.8rem",
                color: "var(--text-tertiary)",
                marginTop: "0.25rem",
              }}
            >
              Creating a new project? This field is locked to maintain Dashpivot
              synchronization.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="edit-jobAddress" className="form-label">
              Job Address
            </label>
            <input
              id="edit-jobAddress"
              type="text"
              className="form-input"
              value={jobAddress}
              onChange={(e) => setJobAddress(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-projectDetails" className="form-label">
              Project Details
            </label>
            <textarea
              id="edit-projectDetails"
              className="form-input"
              rows={3}
              value={projectDetails}
              onChange={(e) => setProjectDetails(e.target.value)}
            />
          </div>

          {/* ── PUBLIC WEBSITE ── */}
          <div
            className="form-group"
            style={{ borderTop: "1px solid var(--border-color)", paddingTop: "var(--spacing-lg)" }}
          >
            <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Globe size={16} /> Public website
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                marginBottom: "var(--spacing-md)",
              }}
            >
              <input
                type="checkbox"
                checked={showOnWeb}
                onChange={(e) => setShowOnWeb(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                Show this project on the SEQ Formwork website
              </span>
            </label>
            <label className="form-label" htmlFor="edit-webDescription">
              Website description
            </label>
            <WebDescriptionEditor value={webDescription} onChange={setWebDescription} />
            <p className="web-editor__hint">
              Public marketing copy shown on the website. Write naturally using the toolbar, no formatting
              codes needed. Keep it public-safe: no phone numbers, emails or PO numbers. (Your internal Project
              Details above are never shown on the website.)
            </p>

            <details style={{ marginTop: "var(--spacing-md)" }}>
              <summary style={{ cursor: "pointer", fontSize: "0.85rem", color: "var(--text-secondary)", fontWeight: 500 }}>
                Website display options (optional)
              </summary>
              <p className="web-editor__hint" style={{ marginTop: 4 }}>
                Leave blank to auto-fill from the job location and address.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "var(--spacing-md)", marginTop: "var(--spacing-sm)" }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="edit-webTitle">Display name</label>
                  <input id="edit-webTitle" className="form-input" value={webTitle} onChange={(e) => setWebTitle(e.target.value)} placeholder="e.g. Retreat" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="edit-webSuburb">Suburb</label>
                  <input id="edit-webSuburb" className="form-input" value={webSuburb} onChange={(e) => setWebSuburb(e.target.value)} placeholder="e.g. Palm Beach" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="edit-webRegion">Region</label>
                  <select id="edit-webRegion" className="form-input" value={webRegion} onChange={(e) => setWebRegion(e.target.value)}>
                    <option value="">Auto</option>
                    <option value="Brisbane">Brisbane</option>
                    <option value="Gold Coast">Gold Coast</option>
                    <option value="Sunshine Coast">Sunshine Coast</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="edit-webType">Type</label>
                  <input id="edit-webType" className="form-input" value={webType} onChange={(e) => setWebType(e.target.value)} placeholder="e.g. Apartments" />
                </div>
              </div>
            </details>
          </div>

          {/* ── PROJECT IMAGES ── */}
          <div className="form-group">
            <label
              className="form-label"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <ImagePlus size={16} />
              Project Photos
              {images.length > 0 && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    color: "var(--text-muted)",
                    fontWeight: 400,
                  }}
                >
                  ({images.length} photo{images.length !== 1 ? "s" : ""})
                </span>
              )}
            </label>
            <ProjectImageUploader
              images={images}
              uploading={uploading}
              onUpload={async (files) => {
                for (const file of files) {
                  await upload(file);
                }
              }}
              onDelete={remove}
              onMakeCover={makeCover}
              onReorder={reorder}
              compact={true}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "var(--spacing-md)",
              alignItems: "start",
            }}
          >
            <div className="form-group">
              <label htmlFor="edit-normalRate" className="form-label">
                Normal Day Labour Rate ($)
              </label>
              <input data-seq-private
                id="edit-normalRate"
                type="number"
                className="form-input"
                value={normalRate}
                onChange={(e) => setNormalRate(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-overtimeRate" className="form-label">
                Overtime Day Labour Rate ($)
              </label>
              <input data-seq-private
                id="edit-overtimeRate"
                type="number"
                className="form-input"
                value={overtimeRate}
                onChange={(e) => setOvertimeRate(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>

            {/* SEQ-169 follow up: claim day and payment period are commercial
                terms, so they are shown only to a user with financial access.
                The RLS policies from 20260915000200 refuse the write
                regardless, and the save above skips it entirely, so hiding the
                inputs is the display half of a gate and not the gate itself. */}
            {canEditClaimTiming && (
              <>
                <div className="form-group">
                  <label htmlFor="edit-claimDay" className="form-label">
                    Claim Day (1-31)
                  </label>
                  <input data-seq-private
                    id="edit-claimDay"
                    type="number"
                    className="form-input"
                    value={claimDay}
                    onChange={(e) => setClaimDay(e.target.value)}
                    min="1"
                    max="31"
                    step="1"
                  />
                  <p
                    style={{
                      fontSize: "0.7rem",
                      color: "var(--text-muted)",
                      marginTop: 4,
                    }}
                  >
                    Day of month each claim defaults to. Clamped to the end of
                    shorter months.
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-paymentPeriodDays" className="form-label">
                    Payment Period (days)
                  </label>
                  <input data-seq-private
                    id="edit-paymentPeriodDays"
                    type="number"
                    className="form-input"
                    value={paymentPeriodDays}
                    onChange={(e) => setPaymentPeriodDays(e.target.value)}
                    min="0"
                    max="365"
                    step="1"
                  />
                  <p
                    style={{
                      fontSize: "0.7rem",
                      color: "var(--text-muted)",
                      marginTop: 4,
                    }}
                  >
                    Calendar days from claim date to due date. Existing claims keep
                    the period they were issued with.
                  </p>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="edit-saturdayRate" className="form-label">
                Saturday Rate: Non-Contract ($)
              </label>
              <input data-seq-private
                id="edit-saturdayRate"
                type="number"
                className="form-input"
                value={saturdayRate}
                onChange={(e) => setSaturdayRate(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-saturdayContractOTRate" className="form-label">
                Saturday Rate: Contract OT ($)
              </label>
              <input data-seq-private
                id="edit-saturdayContractOTRate"
                type="number"
                className="form-input"
                placeholder={overtimeRate || "Uses Overtime Rate"}
                value={saturdayContractOTRate}
                onChange={(e) => setSaturdayContractOTRate(e.target.value)}
                min="0"
                step="0.01"
              />
              <p
                style={{
                  fontSize: "0.7rem",
                  color: "var(--text-muted)",
                  marginTop: 4,
                }}
              >
                Defaults to Overtime Rate if empty
              </p>
            </div>
          </div>

          {/* Retention Toggle */}
          <div
            style={{
              marginTop: "var(--spacing-md)",
              padding: "var(--spacing-md)",
              background: "var(--bg-tertiary)",
              borderRadius: "var(--border-radius-md)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-md)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--spacing-md)", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: 2 }}>
                  Retention Ceiling Base
                </p>
                <p style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  The retention ceiling % is applied to this base
                </p>
              </div>

              {/* Modern 3-option pill selector */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  borderRadius: "8px",
                  background: "var(--bg-secondary, #f1f5f9)",
                  padding: "4px",
                  flexShrink: 0,
                  alignItems: "center",
                }}
              >
                {([
                  { key: 'original' as const, label: 'Original' },
                  { key: 'revised' as const, label: 'Revised' },
                  { key: 'custom' as const, label: 'Custom' },
                ]).map((opt) => {
                  const isActive = retentionCeilingMode === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setRetentionCeilingMode(opt.key)}
                      style={{
                        padding: "6px 14px",
                        fontSize: "0.75rem",
                        fontWeight: isActive ? 600 : 500,
                        border: "none",
                        borderRadius: "6px",
                        background: isActive ? "var(--bg-primary, #ffffff)" : "transparent",
                        color: isActive ? "var(--text-primary)" : "var(--text-muted)",
                        boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom amount input - full width below */}
            {retentionCeilingMode === 'custom' && (
              <div style={{
                paddingTop: "var(--spacing-md)",
                borderTop: "1px solid var(--border-color)",
                marginTop: 4
              }}>
                <label className="form-label" style={{ fontSize: "0.80rem", fontWeight: 500, marginBottom: 8, display: "block" }}>
                  Locked-In Contract Value Base
                </label>
                <div style={{ position: "relative", maxWidth: "300px" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "0.875rem" }}>$</span>
                  <input data-seq-private
                    type="number"
                    className="form-input"
                    placeholder="e.g. 5,000,000"
                    value={retentionCustomAmount}
                    onChange={(e) => setRetentionCustomAmount(e.target.value)}
                    style={{ paddingLeft: 24, width: "100%" }}
                  />
                </div>
                {retentionCustomAmount && (
                  <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--bg-primary, #ffffff)", borderRadius: 6, border: "1px solid var(--border-color)", display: "inline-block" }}>
                    <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: 0 }}>
                      Retention ceiling ({formatRetentionCeilingPercent(retentionCeilingPercent)}%): <span data-seq-private="" style={{ fontWeight: 600, color: "var(--text-primary)" }}>${retentionCeilingAmount(parseFloat(retentionCustomAmount) || 0, retentionCeilingPercent).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* SEQ-235: ceiling percentage, next to the base control */}
            <div style={{ paddingTop: "var(--spacing-md)", borderTop: "1px solid var(--border-color)", marginTop: 4, marginBottom: "var(--spacing-md)" }}>
              <label htmlFor="edit-retention-ceiling-percent" className="form-label" style={{ fontSize: "0.80rem", fontWeight: 500, marginBottom: 8, display: "block" }}>
                Retention ceiling %
              </label>
              <div style={{ position: "relative", maxWidth: "140px" }}>
                <input
                  id="edit-retention-ceiling-percent"
                  data-seq-private
                  type="number"
                  inputMode="decimal"
                  className="form-input form-input--percent"
                  min={0.5}
                  max={100}
                  step={0.5}
                  value={retentionCeilingPercent}
                  readOnly={!canEditClaimTiming}
                  aria-readonly={!canEditClaimTiming}
                  aria-describedby="edit-retention-ceiling-percent-help"
                  onChange={(e) => setRetentionCeilingPercent(e.target.value)}
                  style={{ paddingRight: 28, width: "100%" }}
                />
                <span aria-hidden="true" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "0.875rem" }}>%</span>
              </div>
              <p id="edit-retention-ceiling-percent-help" style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "6px 0 0" }}>
                {canEditClaimTiming ? "Share of the base that retention held can reach. Usually 5." : "Only a user with financial access can change this."}
              </p>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="edit-status" className="form-label">
              Project Status
            </label>
            <select
              id="edit-status"
              className="form-input"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as "active" | "completed" | "on_hold" | "upcoming")
              }
            >
              <option value="active">Active</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
            </select>
          </div>

          {/* SEQ-180 phase 2, P22: project people (free text + datalist). */}
          <ProjectPeopleFields
            idPrefix="edit"
            enabled={isOpen}
            projectManager={projectManager}
            siteManager={siteManager}
            onProjectManagerChange={setProjectManager}
            onSiteManagerChange={setSiteManager}
          />

          {/* Builder Details Section */}
          <div
            style={{
              marginTop: "var(--spacing-lg)",
              padding: "var(--spacing-lg)",
              background: "var(--bg-tertiary)",
              borderRadius: "var(--border-radius-lg)",
            }}
          >
            <h4 style={{ marginBottom: "var(--spacing-md)" }}>
              Builder Details
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--spacing-lg)",
              }}
            >
              <div className="form-group">
                <label htmlFor="edit-builderName" className="form-label">
                  Builder Name
                </label>
                <input
                  id="edit-builderName"
                  type="text"
                  className="form-input"
                  placeholder="e.g., Acme Construction Pty Ltd"
                  value={builderName}
                  onChange={(e) => setBuilderName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-builderEmail" className="form-label">
                  Builder Email
                </label>
                <input
                  id="edit-builderEmail"
                  type="email"
                  className="form-input"
                  placeholder="e.g., contact@builder.com"
                  value={builderEmail}
                  onChange={(e) => setBuilderEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="edit-builderAddress" className="form-label">
                Builder Address
              </label>
              <input
                id="edit-builderAddress"
                type="text"
                className="form-input"
                placeholder="e.g., 123 Main St, Brisbane QLD 4000"
                value={builderAddress}
                onChange={(e) => setBuilderAddress(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="edit-builderPocName" className="form-label">
                Builder POC (Point of Contact) Name
              </label>
              <input
                id="edit-builderPocName"
                type="text"
                className="form-input"
                placeholder="e.g., John Smith"
                value={builderPocName}
                onChange={(e) => setBuilderPocName(e.target.value)}
              />
            </div>
          </div>

          {/* Discounts Section */}
          <div
            style={{
              marginTop: "var(--spacing-lg)",
              padding: "var(--spacing-lg)",
              border: "1px dashed var(--border-color)",
              borderRadius: "var(--border-radius-lg)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "var(--spacing-md)",
              }}
            >
              <h4 style={{ margin: 0 }}>Discounts</h4>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setDiscounts([...discounts, { description: "", amount: "" }])
                }
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <Plus size={16} />
                Add Discount
              </button>
            </div>

            {discounts.length === 0 ? (
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "var(--text-muted)",
                  fontStyle: "italic",
                }}
              >
                No discounts applied.
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--spacing-sm)",
                }}
              >
                {discounts.map((discount, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "var(--spacing-sm)",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Description (e.g. Retention)"
                      value={discount.description}
                      onChange={(e) => {
                        const newDiscounts = [...discounts];
                        newDiscounts[index].description = e.target.value;
                        setDiscounts(newDiscounts);
                      }}
                      style={{ flex: 2 }}
                    />
                    <div style={{ position: "relative", flex: 1 }}>
                      <span
                        style={{
                          position: "absolute",
                          left: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--text-muted)",
                        }}
                      >
                        $
                      </span>
                      <input data-seq-private
                        type="number"
                        className="form-input"
                        placeholder="0.00"
                        value={discount.amount}
                        onChange={(e) => {
                          const newDiscounts = [...discounts];
                          newDiscounts[index].amount = e.target.value;
                          setDiscounts(newDiscounts);
                        }}
                        style={{ paddingLeft: 24 }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ color: "var(--color-error)", padding: 8 }}
                      onClick={() => {
                        setDiscounts(discounts.filter((_, i) => i !== index));
                      }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save size={16} /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
